import { Link } from 'react-router-dom';
import { ArrowRight, Settings } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useIntegrationStatus } from '@/features/settings';
import { useChannels } from '../queries';

interface ChannelSourcePageProps {
  source: 'upwork' | 'linkedin';
  title: string;
  description: string;
  icon: LucideIcon;
  /** Key into `/integrations/status/` for the configured check. */
  integrationKey: string;
}

/** Render a single channel-source dashboard for Upwork or LinkedIn.
 *
 *  Uses the existing `channel_breakdown` analytics endpoint (real CRM data)
 *  and surfaces an integration-not-configured banner from `/integrations/status/`.
 *  Heavier analytics (profile metrics, post engagement) need the full pull from
 *  `integrations/clients/social.py` — deferred to Wave 11. */
export function ChannelSourcePage({ source, title, description, icon: Icon, integrationKey }: ChannelSourcePageProps) {
  const channels = useChannels();
  const status = useIntegrationStatus();

  const row = (channels.data ?? []).find((c) => c.source === source);
  const total = (channels.data ?? []).reduce((s, c) => s + (c.leads ?? 0), 0);
  const share = row && total > 0 ? Math.round(((row.leads ?? 0) / total) * 100) : 0;

  const provider = (status.data ?? []).find((p) => p.key === integrationKey);
  const isConfigured = Boolean(provider?.configured);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Icon className="text-muted-foreground size-6" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
      </div>

      {!status.isPending && !isConfigured ? (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-base">Not connected</CardTitle>
              <CardDescription>
                Add credentials to enable richer profile analytics. Lead inflow below works without it.
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/integrations">
                <Settings className="size-4" /> Integrations
              </Link>
            </Button>
          </CardHeader>
        </Card>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Leads from {title}</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {channels.isPending ? <Skeleton className="h-9 w-16" /> : row?.leads ?? 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Share of total inflow</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {channels.isPending ? <Skeleton className="h-9 w-16" /> : `${share}%`}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground text-xs">
            Of {total} leads across all channels.
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Connection</CardDescription>
            <CardTitle className="text-base">
              {status.isPending ? (
                <Skeleton className="h-5 w-24" />
              ) : isConfigured ? (
                <Badge variant="secondary">Connected</Badge>
              ) : (
                <Badge variant="outline">Not configured</Badge>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {channels.isError ? (
        <p className="text-destructive text-sm">
          {(channels.error as unknown as ApiError)?.message ?? 'Failed to load channel data.'}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Drill in</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground space-y-2 text-sm">
          <Link to={`/crm/leads?source=${source}`} className="hover:text-foreground flex items-center gap-2">
            <ArrowRight className="size-4" /> See all leads from {title}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
